import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Pagination, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BlockIcon from '@mui/icons-material/Block';
import InventoryIcon from '@mui/icons-material/Inventory';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import {
  atualizarManifestoNfe,
  buscarNfeDestinatarioPorChave,
  getEstabelecimentos,
  getNfesDestinatario,
  getNfeDestinatarioXml,
  manifestarNfeDestinatario,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const statusChip = (status) => {
  if (status === 'confirmada') return { color: 'success', label: 'Confirmada' };
  if (status === 'ciencia') return { color: 'info', label: 'Ciência' };
  if (status === 'desconhecimento') return { color: 'warning', label: 'Desconhecimento' };
  if (status === 'nao_realizada') return { color: 'error', label: 'Não realizada' };
  return { color: 'default', label: 'Pendente' };
};

const formatChave = (chave) => {
  const d = String(chave || '').replace(/\D/g, '');
  if (d.length !== 44) return chave || '';
  return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

export default function NfeManifestoPage() {
  const navigate = useNavigate();
  const chaveRef = useRef(null);
  const [estabs, setEstabs] = useState([]);
  const [estabelecimentoId, setEstabelecimentoId] = useState('');
  const [items, setItems] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [pages, setPages] = useState(1);
  const [meta, setMeta] = useState({
    podeAtualizar: true,
    minutosCooldown: 0,
    motivoCooldown: '',
    ultNsu: '',
    maxNsu: '',
    totalItems: 0,
  });
  const [termo, setTermo] = useState('');
  const [chave, setChave] = useState('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState({});
  const [confirmDlg, setConfirmDlg] = useState({ open: false, nota: null, xml: '', origem: 'chave' });
  const [justDlg, setJustDlg] = useState({ open: false, nota: null, texto: '' });
  const [saving, setSaving] = useState(false);

  const carregar = useCallback(async (paginaAlvo = 1, estabId = estabelecimentoId, busca = termo) => {
    if (!Number(estabId)) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await getNfesDestinatario({
        estabelecimentoId: Number(estabId),
        pagina: paginaAlvo,
        qtde: 20,
        termo: busca || undefined,
      });
      const lista = data.items || data.Items || [];
      setItems(Array.isArray(lista) ? lista : []);
      setPages(data.totalPages || data.TotalPages || 1);
      setPagina(paginaAlvo);
      setMeta({
        podeAtualizar: !!(data.podeAtualizar ?? data.PodeAtualizar),
        minutosCooldown: data.minutosCooldown || data.MinutosCooldown || 0,
        motivoCooldown: data.motivoCooldown || data.MotivoCooldown || '',
        ultNsu: data.ultNsu || data.UltNsu || '',
        maxNsu: data.maxNsu || data.MaxNsu || '',
        totalItems: data.totalItems || data.TotalItems || (Array.isArray(lista) ? lista.length : 0),
      });
      setSelected({});
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível listar as NF-e recebidas.'));
    } finally {
      setLoading(false);
    }
  }, [estabelecimentoId, termo]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        const mapped = lista.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
        }));
        setEstabs(mapped);
        if (mapped.length === 1) {
          setEstabelecimentoId(String(mapped[0].id));
        }
      } catch (e) {
        setError(getApiErrorMessage(e, 'Falha ao carregar estabelecimentos.'));
      }
    })();
  }, []);

  useEffect(() => {
    if (!Number(estabelecimentoId)) return;
    carregar(1, estabelecimentoId, termo);
  }, [estabelecimentoId]);

  const irParaEntrada = (xml, estabId = estabelecimentoId) => {
    navigate('/fiscal/importar-xml', {
      state: {
        xml,
        estabelecimentoId: Number(estabId),
      },
    });
  };

  const atualizar = async () => {
    if (!Number(estabelecimentoId)) {
      setError('Selecione o estabelecimento.');
      return;
    }
    setUpdating(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await atualizarManifestoNfe(Number(estabelecimentoId));
      const novos = data.novosDocumentos ?? data.NovosDocumentos ?? 0;
      const msg = data.mensagem || data.Mensagem || 'Listagem atualizada.';
      setSuccess(novos > 0 ? `${msg} (${novos} nova(s) na grade).` : msg);
      await carregar(1);
      if (data.podeContinuar || data.PodeContinuar) {
        setSuccess((s) => `${s} Ainda há documentos na SEFAZ — clique em Atualizar de novo.`);
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao atualizar a listagem na SEFAZ.'));
    } finally {
      setUpdating(false);
    }
  };

  const buscarChave = async (valor = chave) => {
    const digits = String(valor || '').replace(/\D/g, '');
    if (digits.length !== 44) {
      setError('Bipe ou informe a chave de acesso com 44 dígitos.');
      return;
    }
    if (!Number(estabelecimentoId)) {
      setError('Selecione o estabelecimento.');
      return;
    }
    if (searching) return;
    setSearching(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await buscarNfeDestinatarioPorChave(Number(estabelecimentoId), digits);
      setChave('');
      await carregar(1);
      setConfirmDlg({
        open: true,
        nota: data.nota,
        xml: data.xml || '',
        origem: 'chave',
      });
      setSuccess(data.mensagem || 'Nota localizada.');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível localizar a NF-e pela chave.'));
    } finally {
      setSearching(false);
    }
  };

  const onChaveChange = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 44);
    setChave(digits);
    if (digits.length === 44) {
      buscarChave(digits);
    }
  };

  const manifestar = async (nota, tpEvento, justificativa, baixarXml = true) => {
    setSaving(true);
    setError('');
    try {
      const { data } = await manifestarNfeDestinatario(
        nota.id,
        Number(estabelecimentoId),
        { tpEvento, justificativa, baixarXml },
      );
      setSuccess(data.mensagem || 'Evento registrado na SEFAZ.');
      setConfirmDlg({ open: false, nota: null, xml: '', origem: 'chave' });
      setJustDlg({ open: false, nota: null, texto: '' });
      await carregar(pagina);
      if (tpEvento === 210200 && data.xmlCompleto && data.xml) {
        irParaEntrada(data.xml);
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao manifestar a NF-e.'));
    } finally {
      setSaving(false);
    }
  };

  const darEntrada = async (nota) => {
    try {
      if (nota.xmlCompleto) {
        const { data } = await getNfeDestinatarioXml(nota.id, Number(estabelecimentoId));
        if (!data.xmlCompleto) {
          setError('XML ainda não está completo. Confirme a operação para baixar.');
          return;
        }
        irParaEntrada(data.xml);
        return;
      }
      setConfirmDlg({ open: true, nota, xml: '', origem: 'lista' });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível obter o XML.'));
    }
  };

  const confirmarOperacao = async () => {
    const nota = confirmDlg.nota;
    if (!nota) return;
    if (nota.statusManifesto === 'confirmada' && (confirmDlg.xml || nota.xmlCompleto)) {
      if (confirmDlg.xml) {
        irParaEntrada(confirmDlg.xml);
        setConfirmDlg({ open: false, nota: null, xml: '', origem: 'chave' });
        return;
      }
      await darEntrada(nota);
      setConfirmDlg({ open: false, nota: null, xml: '', origem: 'chave' });
      return;
    }
    await manifestar(nota, 210200, undefined, true);
  };

  const idsSelecionados = Object.keys(selected).filter((k) => selected[k]).map(Number);
  const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next = {};
    items.forEach((n) => { next[n.id] = true; });
    setSelected(next);
  };

  const bulk = async (tpEvento) => {
    if (idsSelecionados.length === 0) return;
    setSaving(true);
    setError('');
    let ok = 0;
    try {
      for (const id of idsSelecionados) {
        const nota = items.find((n) => n.id === id);
        if (!nota) continue;
        await manifestarNfeDestinatario(id, Number(estabelecimentoId), {
          tpEvento,
          baixarXml: false,
        });
        ok += 1;
      }
      setSuccess(`${ok} evento(s) enviado(s).`);
      await carregar(pagina);
    } catch (e) {
      setError(getApiErrorMessage(e, `Falha após ${ok} evento(s).`));
      await carregar(pagina);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Manifestação do destinatário"
        subtitle="Atualize a listagem com DistDFe (NSU) e bipe a chave de 44 dígitos para confirmar e dar entrada."
        actions={(
          <Button
            variant="contained"
            startIcon={updating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={updating || !Number(estabelecimentoId) || !meta.podeAtualizar}
            onClick={atualizar}
          >
            Atualizar listagem
          </Button>
        )}
      />

      <Stack spacing={2} sx={{ mb: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <Alert severity="warning">
          A SEFAZ trata consulta repetida como consumo indevido. Não há atualização automática:
          a listagem em massa usa o último NSU gravado, para se não houver documento novo
          e a busca por chave só ocorre quando você bipar os 44 dígitos.
          {meta.motivoCooldown
            ? ` ${meta.motivoCooldown} Libera em cerca de ${meta.minutosCooldown} min.`
            : ''}
        </Alert>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Estabelecimento"
          value={estabelecimentoId}
          onChange={(e) => {
            setEstabelecimentoId(e.target.value);
            carregar(1, e.target.value, termo);
          }}
          sx={{ minWidth: 260 }}
        >
          {estabs.map((e) => (
            <MenuItem key={e.id} value={String(e.id)}>{e.nome}</MenuItem>
          ))}
        </TextField>
        <TextField
          inputRef={chaveRef}
          label="Bipar chave de acesso"
          placeholder="44 dígitos da NF-e"
          value={chave}
          onChange={(e) => onChaveChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              buscarChave();
            }
          }}
          inputProps={{ inputMode: 'numeric', maxLength: 44 }}
          helperText={`${chave.length}/44`}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
          disabled={searching || chave.length !== 44}
          onClick={() => buscarChave()}
        >
          Buscar
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          label="Filtrar na listagem local"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') carregar(1); }}
          sx={{ minWidth: 280 }}
        />
        <Typography variant="body2" color="text.secondary">
          {meta.totalItems} nota(s) · NSU {meta.ultNsu || '—'} / máx. {meta.maxNsu || '—'}
        </Typography>
        {idsSelecionados.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={saving} onClick={() => bulk(210210)}>
              Ciência ({idsSelecionados.length})
            </Button>
            <Button size="small" disabled={saving} onClick={() => bulk(210200)}>
              Confirmar ({idsSelecionados.length})
            </Button>
          </Stack>
        )}
      </Stack>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={items.length > 0 && idsSelecionados.length === items.length}
                  indeterminate={idsSelecionados.length > 0 && idsSelecionados.length < items.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Emissão</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Emitente</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>XML</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center"><CircularProgress size={22} /></TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary">
                    Nenhuma NF-e recebida neste estabelecimento. Atualize a listagem ou bipe uma chave.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map((n) => {
              const st = statusChip(n.statusManifesto);
              return (
                <TableRow key={n.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!!selected[n.id]}
                      onChange={(e) => setSelected((s) => ({ ...s, [n.id]: e.target.checked }))}
                    />
                  </TableCell>
                  <TableCell>
                    {n.dataEmissao
                      ? new Date(n.dataEmissao).toLocaleDateString('pt-BR')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatChave(n.chave)}>
                      <span>{n.numero}/{n.serie}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{n.nomeEmitente || n.cnpjEmitente || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{n.cnpjEmitente}</Typography>
                  </TableCell>
                  <TableCell align="right">{money(n.valor)}</TableCell>
                  <TableCell><Chip size="small" color={st.color} label={st.label} /></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={n.xmlCompleto ? 'success' : 'default'}
                      label={n.xmlCompleto ? 'Completo' : 'Resumo'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {n.statusManifesto === 'pendente' && (
                        <Tooltip title="Ciência da emissão (210210)">
                          <span>
                            <Button
                              size="small"
                              onClick={() => manifestar(n, 210210, undefined, false)}
                              disabled={saving}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {n.statusManifesto !== 'confirmada' && n.statusManifesto !== 'desconhecimento' && n.statusManifesto !== 'nao_realizada' && (
                        <Tooltip title="Confirmar operação (210200)">
                          <span>
                            <Button size="small" onClick={() => setConfirmDlg({ open: true, nota: n, xml: '', origem: 'lista' })} disabled={saving}>
                              <CheckCircleOutlineIcon fontSize="small" />
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {n.statusManifesto === 'pendente' && (
                        <>
                          <Tooltip title="Desconhecimento (210220)">
                            <span>
                              <Button size="small" onClick={() => manifestar(n, 210220, undefined, false)} disabled={saving}>
                                <HighlightOffIcon fontSize="small" />
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Operação não realizada (210240)">
                            <span>
                              <Button size="small" onClick={() => setJustDlg({ open: true, nota: n, texto: '' })} disabled={saving}>
                                <BlockIcon fontSize="small" />
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={n.nfeImportadaId ? 'Já importada' : 'Dar entrada'}>
                        <span>
                          <Button
                            size="small"
                            disabled={!!n.nfeImportadaId || saving}
                            onClick={() => darEntrada(n)}
                          >
                            <InventoryIcon fontSize="small" />
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ResponsiveTable>
      <Stack alignItems="center" sx={{ mt: 1 }}>
        <Pagination count={pages} page={pagina} onChange={(_e, p) => carregar(p)} color="primary" />
      </Stack>

      <Dialog open={confirmDlg.open} onClose={() => setConfirmDlg({ open: false, nota: null, xml: '', origem: 'chave' })} fullWidth maxWidth="sm">
        <DialogTitle>Confirmar operação</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              NF {confirmDlg.nota?.numero}/{confirmDlg.nota?.serie} — {confirmDlg.nota?.nomeEmitente}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatChave(confirmDlg.nota?.chave)}
            </Typography>
            <Typography variant="body2">
              Valor {money(confirmDlg.nota?.valor)}
            </Typography>
            <Alert severity="info">
              Envia o evento 210200 (Confirmação da Operação) à SEFAZ, baixa o XML completo
              se ainda não estiver no cache e abre a entrada de estoque.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg({ open: false, nota: null, xml: '', origem: 'chave' })}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={confirmarOperacao}>
            {saving ? 'Enviando...' : 'Confirmar e dar entrada'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={justDlg.open} onClose={() => setJustDlg({ open: false, nota: null, texto: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Operação não realizada</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Justificativa (mín. 15 caracteres)"
            value={justDlg.texto}
            onChange={(e) => setJustDlg((d) => ({ ...d, texto: e.target.value }))}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJustDlg({ open: false, nota: null, texto: '' })}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving || (justDlg.texto || '').trim().length < 15}
            onClick={() => manifestar(justDlg.nota, 210240, justDlg.texto.trim(), false)}
          >
            Enviar 210240
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
